import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectAPI, userAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FiPlus, FiCopy, FiLayout, FiX, FiCheck, FiChevronRight } from 'react-icons/fi';

const TYPE_COLORS = {
  web:    { bg: '#dbeafe', txt: '#1e40af' },
  mobile: { bg: '#ede9fe', txt: '#5b21b6' },
  design: { bg: '#fce7f3', txt: '#9d174d' },
  seo:    { bg: '#d1fae5', txt: '#065f46' },
  other:  { bg: '#f1f5f9', txt: '#475569' },
};

export default function ProjectTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [launchModal, setLaunchModal] = useState(null);
  const [form, setForm] = useState({ name: '', client: '', budget: '', startDate: '', deadline: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [tR, uR] = await Promise.all([projectAPI.getTemplates(), userAPI.getAll()]);
      setTemplates(tR.data.templates || []);
      setUsers((uR.data.users || []).filter(u => u.role === 'client'));
    } catch { toast.error('Failed to load templates'); }
    setLoading(false);
  };

  const launchProject = async () => {
    if (!form.name.trim()) return toast.error('Project name required');
    if (!form.client)      return toast.error('Select a client');
    try {
      const r = await projectAPI.createFromTemplate(launchModal._id, form);
      toast.success('Project created from template!');
      navigate(`/projects/${r.data.project._id}`);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const openLaunch = (t) => {
    setLaunchModal(t);
    setForm({ name: t.templateName ? t.templateName.replace(' Template', '') : t.name, client: '', budget: '', startDate: '', deadline: '' });
  };

  const S = {
    page: { maxWidth: 1200, margin: '0 auto', fontFamily: "'Sora',sans-serif" },
    card: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px #0000000a', border: '1px solid #f1f5f9' },
    btn:  (v) => ({ padding: '9px 18px', borderRadius: 10, border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
      ...(v === 'primary' ? { background: 'var(--pm-gradient)', color: '#fff' }
        : { background: '#f8fafc', color: '#64748b', border: '1.5px solid #e2e8f0' }) }),
    inp:  { width: '100%', padding: '9px 13px', border: '2px solid #e2e8f0', borderRadius: 9, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginTop: 6 },
    overlay: { position: 'fixed', inset: 0, background: '#00000060', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
    modal:   { background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' },
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTop: '3px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap'); @keyframes spin{to{transform:rotate(360deg)}} .tmpl-card:hover{transform:translateY(-3px);box-shadow:0 8px 24px #0000001a!important;}`}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#0f172a' }}>📐 Project Templates</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>Save project structures as reusable templates, then launch with one click</p>
        </div>
      </div>

      {/* How to save tip */}
      <div style={{ background: 'linear-gradient(135deg,#eff6ff,#f0fdf4)', border: '1px solid #bfdbfe', borderRadius: 14, padding: '16px 20px', marginBottom: 28, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ fontSize: 24 }}>💡</div>
        <div>
          <div style={{ fontWeight: 700, color: '#1e40af', fontSize: 14 }}>How to create a template</div>
          <div style={{ fontSize: 13, color: '#3b82f6', marginTop: 2 }}>
            Open any project → click <strong>⋯ More</strong> → <strong>Save as Template</strong>. The project's tasks and structure will be saved here.
          </div>
        </div>
      </div>

      {templates.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', padding: 64 }}>
          <FiLayout size={48} color="#e2e8f0" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>No templates yet</div>
          <div style={{ color: '#94a3b8', fontSize: 14 }}>Save an existing project as a template to start building your library</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 20 }}>
          {templates.map(t => {
            const tc = TYPE_COLORS[t.projectType] || TYPE_COLORS.other;
            return (
              <div key={t._id} className="tmpl-card" style={{ ...S.card, transition: 'all .2s', cursor: 'default' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 17, color: '#0f172a', marginBottom: 6 }}>{t.templateName || t.name}</div>
                    <span style={{ ...tc, padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{t.projectType}</span>
                  </div>
                  <div style={{ background: '#f1f5f9', borderRadius: 10, padding: '6px 12px', textAlign: 'center', flexShrink: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--pm-primary)' }}>{t.templateTasks?.length || 0}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>TASKS</div>
                  </div>
                </div>

                {t.description && <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.6, margin: '0 0 16px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.description}</p>}

                {/* Task preview */}
                {(t.templateTasks || []).slice(0, 4).map((task, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f8fafc' }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: { critical: '#7c3aed', high: '#ef4444', medium: '#f59e0b', low: '#22c55e' }[task.priority] || '#94a3b8', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#475569', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</span>
                    {task.estimatedHours > 0 && <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{task.estimatedHours}h</span>}
                  </div>
                ))}
                {(t.templateTasks || []).length > 4 && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>+{t.templateTasks.length - 4} more tasks</div>
                )}

                <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center', marginTop: 20 }} onClick={() => openLaunch(t)}>
                  <FiCopy size={14} /> Use This Template
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Launch modal */}
      {launchModal && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setLaunchModal(null)}>
          <div style={S.modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Launch Project</h3>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>From: <strong>{launchModal.templateName || launchModal.name}</strong></div>
              </div>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#94a3b8' }} onClick={() => setLaunchModal(null)}><FiX /></button>
            </div>

            {/* Template tasks preview */}
            {(launchModal.templateTasks || []).length > 0 && (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>{launchModal.templateTasks.length} TASKS WILL BE CREATED</div>
                {launchModal.templateTasks.slice(0, 5).map((t, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', marginBottom: 4 }}>
                    <FiCheck size={11} color="#22c55e" />{t.title}
                  </div>
                ))}
                {launchModal.templateTasks.length > 5 && <div style={{ fontSize: 11, color: '#94a3b8' }}>+{launchModal.templateTasks.length - 5} more</div>}
              </div>
            )}

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 14 }}>
              Project Name *
              <input style={S.inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Enter project name" />
            </label>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 14 }}>
              Client *
              <select style={S.inp} value={form.client} onChange={e => setForm({ ...form, client: e.target.value })}>
                <option value="">Select client</option>
                {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
              </select>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 14 }}>
                Budget ($)
                <input type="number" style={S.inp} value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} placeholder="0" />
              </label>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 14 }}>
                Start Date
                <input type="date" style={S.inp} value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
              </label>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 14, gridColumn: '1/-1' }}>
                Deadline
                <input type="date" style={S.inp} value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button style={S.btn('ghost')} onClick={() => setLaunchModal(null)}>Cancel</button>
              <button style={S.btn('primary')} onClick={launchProject}><FiChevronRight /> Launch Project</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
